import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Stack, Title, Text, Button } from '@mantine/core'

interface EmptyStateProps {
  /** Título principal — versão antiga; equivalente a `title` */
  title: string
  description?: string
  /** Action via callback (versão antiga, mantida para retrocompat) */
  action?: { label: string; onClick: () => void }
  /** Action via link route — versão nova, recomendada */
  actionTo?: string
  actionLabel?: string
  /** CTA secundário (ex: importar planilha) */
  secondaryActionTo?: string
  secondaryActionLabel?: string
  /** Ícone emoji opcional acima do título — versão nova */
  icon?: string
  /** Children livre para compor extras (ex: dicas, links auxiliares) */
  children?: ReactNode
}

/**
 * Empty state para listas vazias.
 *
 * Versão antiga (compat): { title, description, action: { label, onClick } }
 * Versão nova: { title, description, icon, actionTo, actionLabel,
 *               secondaryActionTo, secondaryActionLabel }
 *
 * UX: ícone grande + título + descrição + 1-2 CTAs em botões. Visível
 * em tenants zerados (onboarding) e quando filtros não retornam nada.
 */
export function EmptyState({
  title,
  description,
  action,
  actionTo,
  actionLabel,
  secondaryActionTo,
  secondaryActionLabel,
  icon,
  children,
}: EmptyStateProps) {
  return (
    <Stack align="center" justify="center" h={320} gap="sm" data-testid="empty-state">
      {icon && (
        <Text size="3rem" lh={1} aria-hidden="true">
          {icon}
        </Text>
      )}
      <Title order={4} c="dimmed">
        {title}
      </Title>
      {description && (
        <Text size="sm" c="dimmed" ta="center" maw={460}>
          {description}
        </Text>
      )}
      {action && (
        <Button variant="light" mt="xs" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {actionTo && actionLabel && (
        <Stack gap="xs" align="center" mt="xs">
          <Button
            component={Link}
            to={actionTo}
            variant="filled"
            c="white"
            data-testid="empty-state-cta-primary"
          >
            {actionLabel}
          </Button>
          {secondaryActionTo && secondaryActionLabel && (
            <Button
              component={Link}
              to={secondaryActionTo}
              variant="light"
              c="brand.9"
              data-testid="empty-state-cta-secondary"
            >
              {secondaryActionLabel}
            </Button>
          )}
        </Stack>
      )}
      {children}
    </Stack>
  )
}
